import { Link } from 'react-router-dom';
import { money } from '../lib/format';
import { VideoPoster } from './VideoPoster';
import { useSavedHomes } from '../lib/savedHomes';

/**
 * One home, as a card.
 *
 * Shared by the search results, the homepage strip and the "similar homes" row
 * on a detail page, so a listing looks the same wherever it is met.
 */

export interface PublicListing {
  id: string;
  title: string;
  description: string | null;
  unit_type: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  county: string | null;
  estate_area: string | null;
  rent: string;
  deposit: string | null;
  available_from: string | null;
  amenities: string[];
  status: string;
  published_at: string | null;
  photo_ids: string[] | null;
  /** 360° room views, in tour order. Empty when the landlord shot none. */
  panoramas?: ListingPanorama[] | null;
  /** The walkthrough video, if there is one. At most one per listing. */
  video_id?: string | null;
  /** The unit has been taken. Still shown, but not available to enquire on. */
  is_sold_out?: boolean;
  /**
   * The landlord paid for placement (migration 126). Always shown as
   * "Featured" wherever the card appears: paid position is labelled, never
   * passed off as the best match.
   */
  is_featured?: boolean;
  /** When the landlord last confirmed the home is still available (127). */
  availability_confirmed_at?: string | null;
  /** Monthly service charge on top of rent, when the landlord has said. */
  service_charge?: string | null;
  /** Utilities, in the landlord's words ("Water by meter, KPLC tokens"). */
  utilities_note?: string | null;
  /** Short stays (155): furnished, priced by the night. `rent` is then the nightly rate. */
  stay_type?: 'long_term' | 'short_stay';
  nightly_rate?: string | null;
  weekly_rate?: string | null;
  cleaning_fee?: string | null;
  min_nights?: number | null;
  max_guests?: number | null;
  check_in_time?: string | null;
  check_out_time?: string | null;
  house_rules?: string | null;
  booking_url?: string | null;
  tourism_licence?: string | null;
  /** Who posted it (156): landlord, owner, agent, caretaker or individual. */
  poster_role?: string | null;
}

/** "Posted by an agent", for the card and the detail page. Nothing for a landlord's own advert. */
export function posterLabel(role?: string | null): string | null {
  return role === 'owner' ? 'Posted by the owner'
    : role === 'agent' ? 'Posted by an agent'
    : role === 'caretaker' ? 'Posted by a caretaker'
    : role === 'individual' ? 'Posted by an individual'
    : null;
}

/** "/night" or "/month", by what the price is for. */
export const perUnit = (l: { stay_type?: string }) => (l.stay_type === 'short_stay' ? '/night' : '/month');

/**
 * Save or unsave a home. A SIBLING of the card's link, never inside it: a
 * button inside an <a> is invalid markup, and screen readers announce the
 * pair as one confusing control.
 */
export function SaveHomeButton({ id, className = 'lst-save' }: { id: string; className?: string }) {
  const { isSaved, toggle } = useSavedHomes();
  const saved = isSaved(id);
  return (
    <button
      type="button"
      className={`${className}${saved ? ' is-saved' : ''}`}
      aria-pressed={saved}
      aria-label={saved ? 'Remove from saved homes' : 'Save this home'}
      title={saved ? 'Saved' : 'Save'}
      onClick={() => toggle(id)}
    >
      <span aria-hidden>{saved ? '♥' : '♡'}</span>
    </button>
  );
}

/** The photo endpoint is public and the bytes are immutable, so the browser
 *  caches these for a year — see the cache-control on the route. */
export interface ListingPanorama { id: string; section: string; label: string | null }

// Default is the original; the card grid passes 'thumb' (089). Kept a single
// export so the detail gallery (ListingMedia) still gets full-resolution.
export const photoUrl = (id: string, size: 'full' | 'thumb' = 'full') =>
  `/api/v1/discover/photos/${id}${size === 'thumb' ? '?size=thumb' : ''}`;

export function ListingCard({ listing: l }: { listing: PublicListing }) {
  const photo = l.photo_ids?.[0];
  const where = [l.estate_area, l.county].filter(Boolean).join(', ');

  return (
    <div className="lst-card-wrap">
    <Link to={`/homes/${l.id}`}
          className={`lst-card${l.is_sold_out ? ' lst-card-taken' : ''}${l.is_featured && !l.is_sold_out ? ' lst-card-featured' : ''}`}>
      <div className="lst-photo">
        {photo
          ? <img src={photoUrl(photo, 'thumb')} alt="" loading="lazy" />
          // A listing with no photo still has to look deliberate rather than
          // broken, so the placeholder is styled rather than left empty.
          : l.video_id
            ? <VideoPoster src={`/api/v1/discover/videos/${l.video_id}`} />
            : <div className="lst-photo-none" aria-hidden>⌂</div>}
        {/* Over the photo, before the rent is read: whoever is scanning the
            grid should know this one is gone without opening it. */}
        {l.is_sold_out && <span className="lst-taken-badge">Taken</span>}
        {(l.photo_ids?.length ?? 0) > 1 && !l.is_sold_out && (
          <span className="lst-photo-count">{l.photo_ids!.length} photos</span>
        )}
        {/* A tour or a video is the reason to open THIS card rather than the
            one beside it, so it is advertised in the grid. Top-left, clear of
            the photo count and the Taken badge. */}
        {!l.is_sold_out && ((l.panoramas?.length ?? 0) > 0 || l.video_id) && (
          <span className="lst-media-badges">
            {(l.panoramas?.length ?? 0) > 0 && <span className="lst-media-badge">360°</span>}
            {l.video_id && <span className="lst-media-badge">▶ Video</span>}
          </span>
        )}
      </div>

      <div className="lst-body">
        {l.is_featured && !l.is_sold_out && <span className="lst-featured">Featured</span>}
        {l.stay_type === 'short_stay' && <span className="lst-featured lst-short">Short stay</span>}
        <p className="lst-rent">
          {money(l.rent)}<span className="lst-per">{perUnit(l)}</span>
        </p>
        <h3 className="lst-title">{l.title}</h3>
        {where && <p className="lst-where">{where}</p>}

        <p className="lst-facts">
          {posterLabel(l.poster_role) && <span className="lst-type">{posterLabel(l.poster_role)}</span>}
          {l.bedrooms !== null && <span>{l.bedrooms} bed</span>}
          {l.bathrooms !== null && <span>{l.bathrooms} bath</span>}
          {l.unit_type && <span className="lst-type">{l.unit_type}</span>}
          {l.stay_type === 'short_stay' && l.max_guests ? <span>Sleeps {l.max_guests}</span> : null}
        </p>
      </div>
    </Link>
    <SaveHomeButton id={l.id} />
    </div>
  );
}
